import React, { useState, useEffect } from 'react';
import {
  CalculatorTheme,
  CompoundingFrequency,
  CalculatorInputs,
  Results,
  LumpsumSIPCalculatorProps
} from './types';

const defaultTheme: CalculatorTheme = {
  primary: "#0C875E",
  secondary: "#108E66",
  accent: "#D6F5E8",
  background: "#FAFAFA",
  textPrimary: "#333333",
  textSecondary: "#666666"
};

export const LumpsumSIPCalculator: React.FC<LumpsumSIPCalculatorProps> = ({ theme = {} }) => {
  const calculatorTheme = { ...defaultTheme, ...theme };

  const [inputs, setInputs] = useState<CalculatorInputs>({
    lumpsumPrincipal: "",
    lumpsumRate: "",
    sipAmount: "",
    sipRate: "",
    years: "",
  });

  const [lumpsumFrequency, setLumpsumFrequency] = useState<CompoundingFrequency>("Annually");
  const [errors, setErrors] = useState<Partial<CalculatorInputs>>({});
  const [results, setResults] = useState<Results | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationTimer, setCalculationTimer] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (calculationTimer) clearTimeout(calculationTimer);
    };
  }, [calculationTimer]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs(prev => ({ ...prev, [name]: value }));
  };
  
  const handleFrequencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLumpsumFrequency(e.target.value as CompoundingFrequency);
  };

  const validateInputs = (): boolean => {
    const newErrors: Partial<CalculatorInputs> = {};
    let isValid = true;

    // Validate required fields
    Object.entries(inputs).forEach(([key, value]) => {
      if (!value) {
        newErrors[key as keyof CalculatorInputs] = 'This field is required';
        isValid = false;
      }
    });

    // Validate numeric ranges
    const numInputs = {
      lumpsumPrincipal: { min: 1, max: 1000000000 },
      lumpsumRate: { min: 0, max: 100 },
      sipAmount: { min: 1, max: 1000000 },
      sipRate: { min: 0, max: 100 },
      years: { min: 1, max: 50 }
    };

    Object.entries(numInputs).forEach(([key, { min, max }]) => {
      const value = parseFloat(inputs[key as keyof CalculatorInputs]);
      if (isNaN(value) || value < min || value > max) {
        newErrors[key as keyof CalculatorInputs] = `Value must be between ${min} and ${max}`;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleCalculate = () => {
    if (!validateInputs()) return;
    setIsCalculating(true);
    setResults(null);

    const P_lump = parseFloat(inputs.lumpsumPrincipal);
    const r_lump_annual = parseFloat(inputs.lumpsumRate) / 100;
    const nMap = { Annually: 1, "Semi-Annually": 2, Quarterly: 4, Monthly: 12 };
    const n_lump_compound = nMap[lumpsumFrequency];
    const t = parseFloat(inputs.years);

    const P_sip = parseFloat(inputs.sipAmount);
    const r_sip_annual = parseFloat(inputs.sipRate) / 100;
    const r_sip_monthly = r_sip_annual / 12;
    const n_sip_months = t * 12;

    const timer = setTimeout(() => {
      // Lumpsum Calculation
      const lumpsumFutureValue = P_lump * Math.pow(1 + r_lump_annual / n_lump_compound, n_lump_compound * t);
      
      // SIP Calculation
      const sipFutureValue = P_sip * ((Math.pow(1 + r_sip_monthly, n_sip_months) - 1) / r_sip_monthly) * (1 + r_sip_monthly);
      
      const lumpsumInvested = P_lump;
      const sipInvested = P_sip * n_sip_months;
      const totalInvested = lumpsumInvested + sipInvested;
      const combinedFutureValue = lumpsumFutureValue + sipFutureValue;
      const totalWealthGained = combinedFutureValue - totalInvested;

      setResults({
        lumpsumFutureValue,
        sipFutureValue,
        lumpsumInvested,
        sipInvested,
        totalInvested,
        combinedFutureValue,
        totalWealthGained
      });
      setIsCalculating(false);
    }, 500);

    setCalculationTimer(timer);
  };

  return (
    <div style={{ 
      backgroundColor: calculatorTheme.background,
      color: calculatorTheme.textPrimary,
      padding: '20px',
      borderRadius: '8px',
      maxWidth: '800px',
      margin: '0 auto'
    }}>
      <h2 style={{ color: calculatorTheme.primary }}>Lumpsum + SIP Calculator</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <div style={{ marginBottom: '15px' }}>
          <label>Lumpsum Investment (₹)</label>
          <input
            type="number"
            name="lumpsumPrincipal"
            value={inputs.lumpsumPrincipal}
            onChange={handleInputChange}
            style={{
              width: '100%',
              padding: '8px',
              border: `1px solid ${errors.lumpsumPrincipal ? 'red' : calculatorTheme.accent}`,
              borderRadius: '4px'
            }}
          />
          {errors.lumpsumPrincipal && (
            <span style={{ color: 'red', fontSize: '12px' }}>{errors.lumpsumPrincipal}</span>
          )}
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Lumpsum Annual Return (%)</label>
          <input
            type="number"
            name="lumpsumRate"
            value={inputs.lumpsumRate}
            onChange={handleInputChange}
            style={{
              width: '100%',
              padding: '8px',
              border: `1px solid ${errors.lumpsumRate ? 'red' : calculatorTheme.accent}`,
              borderRadius: '4px'
            }}
          />
          {errors.lumpsumRate && (
            <span style={{ color: 'red', fontSize: '12px' }}>{errors.lumpsumRate}</span>
          )}
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Compounding Frequency</label>
          <select
            value={lumpsumFrequency}
            onChange={handleFrequencyChange}
            style={{
              width: '100%',
              padding: '8px',
              border: `1px solid ${calculatorTheme.accent}`,
              borderRadius: '4px'
            }}
          >
            <option value="Annually">Annually</option>
            <option value="Semi-Annually">Semi-Annually</option>
            <option value="Quarterly">Quarterly</option>
            <option value="Monthly">Monthly</option>
          </select>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Monthly SIP Amount (₹)</label>
          <input
            type="number"
            name="sipAmount"
            value={inputs.sipAmount}
            onChange={handleInputChange}
            style={{
              width: '100%',
              padding: '8px',
              border: `1px solid ${errors.sipAmount ? 'red' : calculatorTheme.accent}`,
              borderRadius: '4px'
            }}
          />
          {errors.sipAmount && (
            <span style={{ color: 'red', fontSize: '12px' }}>{errors.sipAmount}</span>
          )}
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>SIP Annual Return (%)</label>
          <input
            type="number"
            name="sipRate"
            value={inputs.sipRate}
            onChange={handleInputChange}
            style={{
              width: '100%',
              padding: '8px',
              border: `1px solid ${errors.sipRate ? 'red' : calculatorTheme.accent}`,
              borderRadius: '4px'
            }}
          />
          {errors.sipRate && (
            <span style={{ color: 'red', fontSize: '12px' }}>{errors.sipRate}</span>
          )}
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label>Investment Duration (Years)</label>
          <input
            type="number"
            name="years"
            value={inputs.years}
            onChange={handleInputChange}
            style={{
              width: '100%',
              padding: '8px',
              border: `1px solid ${errors.years ? 'red' : calculatorTheme.accent}`,
              borderRadius: '4px'
            }}
          />
          {errors.years && (
            <span style={{ color: 'red', fontSize: '12px' }}>{errors.years}</span>
          )}
        </div>
      </div>

      <button
        onClick={handleCalculate}
        disabled={isCalculating}
        style={{
          backgroundColor: calculatorTheme.primary,
          color: 'white',
          padding: '10px 20px',
          border: 'none',
          borderRadius: '4px',
          cursor: isCalculating ? 'not-allowed' : 'pointer',
          opacity: isCalculating ? 0.7 : 1
        }}
      >
        {isCalculating ? 'Calculating...' : 'Calculate'}
      </button>

      {results && (
        <div style={{ marginTop: '20px', padding: '20px', backgroundColor: calculatorTheme.accent, borderRadius: '4px' }}>
          <h3 style={{ color: calculatorTheme.primary }}>Results</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <p>Lumpsum Future Value: ₹{results.lumpsumFutureValue.toLocaleString()}</p>
              <p>SIP Future Value: ₹{results.sipFutureValue.toLocaleString()}</p>
              <p>Total Invested: ₹{results.totalInvested.toLocaleString()}</p>
            </div>
            <div>
              <p>Combined Future Value: ₹{results.combinedFutureValue.toLocaleString()}</p>
              <p>Total Wealth Gained: ₹{results.totalWealthGained.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 